<template>
  <div class="sidebar-top d-print-none">
    <span class="report-icn d-none d-md-block" :class="product.icon"/>
    <h3 class="text-center">{{ header }}</h3>
    <div class="mt-2 mb-2">
      <div class="user-control">
        <span class="icon icon-checkmark-circle"/>
        <div>
          <span v-if="foundUser.name">{{ $t('report.userName') }}: {{ foundUser.name }}</span>
          <span>{{ $t('report.identityCode') }}: {{ foundUser.personId }}</span>
          <div v-for="consent in foundUser.consents">
                <span v-if="consent.productCode === product.id">{{
                    $t('report.consent.label')
                  }}: {{ consentStatusValue(consent.status) }}</span>
          </div>
        </div>
      </div>
      <span class="sidebar-caption">
        {{ this.hintMessage }}
      </span>
    </div>
    <div v-if="!profile.email && !isConsentRequestAlreadyInProcess()">
      <span class="sidebar-caption text-danger">
        {{ $t('report.consent.consentRequestMissingEmailWarning') }}
      </span>
      <button
          name="redirectToProfile"
          type="button"
          class="btn btn-info btn-block"
          @click="redirectToProfile()"
      >
        <span>{{ $t('report.consent.goToProfileButtonLabel') }}</span>
      </button>
    </div>
    <div v-else-if="!isConsentRequestAlreadyInProcess()">
      <button
          name="requestConsent"
          type="button"
          class="btn btn-success btn-block"
          @click="toggleConsentRequestForm()"
      >
        <span v-if="isFirstTimeAskingForConsent()">
          {{ $t('report.consent.askConsentButtonLabel1') }}
        </span>
        <span v-else>{{ $t('report.consent.askConsentButtonLabel2') }}</span>
      </button>
    </div>
    <div v-if="errorMessage.length" class="alert-danger">
      <span>{{ errorMessage }}</span>
    </div>
    <div v-if="successMessage.length" class="alert-success">
      <span>{{ successMessage }}</span>
    </div>
    <div>
      <button
          name="resetConsentRequest"
          type="button"
          class="btn btn-default btn-block mt-2"
          @click="$emit('resetFoundUser')"
      >
        <span v-if="isConsentRequestAlreadyInProcess() || successMessage.length">
          {{ $t('report.consent.resetButtonLabel2') }}
        </span>
        <span v-else>{{ $t('report.consent.resetButtonLabel1') }}</span>
      </button>
    </div>
  </div>

  <BModal v-model="isVisibleConsentRequestForm">
    <template v-slot:modal-header>
      <h4 class="modal-title fw-bold">{{ $t('report.consentFormTitle') }}</h4>
      <button class="close" @click.prevent="toggleConsentRequestForm">
        {{ $t('close') }}<span class="icon-close2"/>
      </button>
    </template>
    <p>
      {{ $t('report.consentFormHint') }}
    </p>
    <p class="fw-bold gray-600">
      {{ $t('report.consentFormEmailLabel') }}
    </p>

    <ValidationField email required :value="subjectEmail" highlight ref="validationField">
      <input type="email" class="form-control" id="email" v-model="subjectEmail">
      <span class="invalid-feedback invalid-feedback-dispute col-sm-12" v-text="$t('report.consentFormInvalidEmail')"/>
    </ValidationField>
    <p class="mt-3">{{ $t('report.consentFormExplanation') }}</p>

    <template v-slot:footer>
      <button class="btn btn-block btn-success" :disabled="!subjectEmail.length" @click="requestConsent">
        {{ $t('report.confirmed') }}
      </button>
      <a class="btn btn-default" @click="toggleConsentRequestForm">
        {{ $t('report.cancel') }}
      </a>
    </template>
  </BModal>
</template>

<script>
import ValidationField from '@/components/ValidationField';
import { mapActions, mapState } from 'vuex';
import { BModal } from 'bootstrap-vue-next';

export default {
  emits: ['resetFoundUser'],
  props: {
    product: {
      type: Object,
      required: true
    },
    foundUser: {
      type: Object,
      required: true
    },
    consent: {
      type: Object,
      required: true
    },
    scope: {
      type: String,
      required: false
    }
  },
  components: {
    BModal,
    ValidationField,
  },
  data() {
    return {
      subjectEmail: '',
      isVisibleConsentRequestForm: false,
      successMessage: '',
      errorMessage: '',
      hintMessage: '',
    };
  },
  computed: {
    ...mapState('session', ['profile']),
    header() {
      return this.scope ? this.$t(`${this.scope}.header`) : this.product.title;
    },
  },
  watch: {
    '$i18n.locale': function () {
      this.buildHintMessage();
    },
  },
  methods: {
    ...mapActions('consentRequests', ['createConsent']),
    ...mapActions('alerts', ['notify']),
    toggleConsentRequestForm() {
      this.isVisibleConsentRequestForm = !this.isVisibleConsentRequestForm;
      this.subjectEmail = '';
    },
    requestConsent() {
      const validationField = this.$refs.validationField.slotProps;
      if (validationField.touched && validationField.valid) {
        let params = {};
        params.subjectCode = this.foundUser.personId;
        params.subjectEmail = this.subjectEmail;
        params.productCode = this.product.id;

        return this.createConsent(params)
            .then(() => {
              this.toggleConsentRequestForm();
              this.consent.status = 'CREATED';
              this.notify({ type: 'success', text: this.$t('report.consent.consentRequestedHint') });
            })
            .catch(() => {
              this.toggleConsentRequestForm();
              this.consent.status = '';
              this.notify({ type: 'danger', text: this.$t('report.consent.consentRequestFailed') });
            });
      }
    },
    isConsentRequestAlreadyInProcess() {
      return this.consent.status === 'CREATED';
    },
    isFirstTimeAskingForConsent() {
      if (this.consent.status === 'USED') {
        // ignore previous used consents and treat it as a new request
        return true;
      }
      return !this.consent.status;

    },
    consentStatusValue(statusCode) {
      if (statusCode === 'APPROVED') {
        return this.$t('report.consent.approved');
      }
      if (statusCode === 'CREATED') {
        return this.$t('report.consent.created');
      }
      if (statusCode === 'DENIED') {
        return this.$t('report.consent.denied');
      }
      if (statusCode === 'EXPIRED') {
        return this.$t('report.consent.expired');
      }
      return this.$t('report.consent.missing');
    },
    buildHintMessage() {
      if (this.isConsentRequestAlreadyInProcess()) {
        this.hintMessage = this.$t('report.consent.consentInProcessHint');
      } else {
        this.hintMessage = this.$t('report.consent.consentRequestHint');
      }
    },
    redirectToProfile() {
      this.$router.push({ name: `profile-${this.$i18n.locale}`, params: { lang: this.$i18n.locale } });
    }
  },
  created() {
    this.buildHintMessage();
  },
};
</script>
